/**
 * PinPad — 4-digit PIN entry for kiosk authentication
 */

import { useState, useCallback } from 'react';

interface PinPadProps {
  onSubmit: (pin: string) => void;
  onCancel: () => void;
  isProcessing?: boolean;
  error?: string | null;
}

export function PinPad({ onSubmit, onCancel, isProcessing, error }: PinPadProps) {
  const [pin, setPin] = useState('');

  const handleDigit = useCallback((digit: string) => {
    setPin(prev => {
      const next = prev + digit;
      if (next.length === 4) {
        onSubmit(next);
        return next;
      }
      return next;
    });
  }, [onSubmit]);

  const handleBackspace = useCallback(() => {
    setPin(prev => prev.slice(0, -1));
  }, []);

  const handleClear = useCallback(() => {
    setPin('');
  }, []);

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', ''];

  return (
    <div className="flex flex-col items-center gap-6">
      {/* PIN dots */}
      <div className="flex gap-4">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className={`w-5 h-5 rounded-full border-2 transition-all ${
              i < pin.length
                ? 'bg-indigo-500 border-indigo-500'
                : 'border-slate-300 bg-white'
            }`}
          />
        ))}
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      {isProcessing && (
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      )}

      {/* Number pad */}
      <div className="grid grid-cols-3 gap-3">
        {digits.map((digit, i) => {
          if (digit === '' && i === 9) {
            return (
              <button
                key="cancel"
                onClick={onCancel}
                className="w-20 h-20 rounded-2xl text-lg font-medium text-slate-500 hover:bg-slate-100 transition-colors"
              >
                Terug
              </button>
            );
          }
          if (digit === '' && i === 11) {
            return (
              <button
                key="backspace"
                onClick={handleBackspace}
                disabled={pin.length === 0}
                className="w-20 h-20 rounded-2xl text-lg font-medium text-slate-500 hover:bg-slate-100 transition-colors disabled:opacity-30"
              >
                <svg className="w-6 h-6 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414-6.414A2 2 0 0110.828 5H17a2 2 0 012 2v10a2 2 0 01-2 2h-6.172a2 2 0 01-1.414-.586L3 12z" />
                </svg>
              </button>
            );
          }
          return (
            <button
              key={digit}
              onClick={() => handleDigit(digit)}
              disabled={pin.length >= 4 || isProcessing}
              className="w-20 h-20 rounded-2xl bg-white border border-slate-200 text-2xl font-semibold text-slate-800 hover:bg-slate-50 active:bg-slate-100 transition-colors disabled:opacity-50 shadow-sm"
            >
              {digit}
            </button>
          );
        })}
      </div>

      {pin.length === 4 && !isProcessing && (
        <button
          onClick={handleClear}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Opnieuw invoeren
        </button>
      )}
    </div>
  );
}
